import { Component, useEffect, useState } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { useAuthStore } from './stores/authStore';
import { useFontStore } from './stores/fontStore';
import { usePanelStore } from './stores/panelStore';
import { useStudioStore } from './stores/studioStore';
import { initCustomFonts } from './lib/fontLoader';
import DevModeIndicator from './components/DevModeIndicator';
import { BugReportButton } from './components/bug-reporter/BugReportButton';
import { DiagnosticWidget } from './components/diagnostics/DiagnosticWidget';
import { DiagnosticToggleFab } from './components/diagnostics/DiagnosticToggleFab';
import LoadingScreen from './components/LoadingScreen';
import Sidebar from './components/Sidebar';
import DashboardPage from './pages/DashboardPage';
import ProjectsPage from './pages/ProjectsPage';
import TemplatesPage from './pages/TemplatesPage';
import AssetsPage from './pages/AssetsPage';
import ExportsPage from './pages/ExportsPage';
import EditorPage from './pages/EditorPage';

class AppErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[CS] React Error Boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-screen w-screen flex items-center justify-center bg-[#0a0a0a] text-gray-200 font-montserrat">
          <div className="text-center space-y-4 max-w-md px-6">
            <div className="text-red-400 text-lg font-semibold">Something went wrong</div>
            <p className="text-sm text-gray-400">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm rounded transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const { initialize, loading, user } = useAuthStore();
  const [fontsReady, setFontsReady] = useState(false);

  useEffect(() => {
    initialize();
    initCustomFonts()
      .then(() => {
        useFontStore.getState().refreshFonts();
        setFontsReady(true);
      })
      .catch((err) => {
        console.error('[App] initCustomFonts failed', err);
        setFontsReady(true);
      });
  }, [initialize]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    const returnUrl = encodeURIComponent(window.location.origin);
    const homeUrl = `https://home.abcotto.com?redirect_to=${returnUrl}`;

    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#0a0a0a] text-gray-200 font-montserrat">
        <div className="text-center space-y-3">
          <p className="text-sm text-gray-400">Not authenticated.</p>
          <p className="text-xs text-gray-600">
            Sign in at{' '}
            <a href={homeUrl} className="text-cyan-400 hover:underline">
              home.abcotto.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (!fontsReady) {
    return <LoadingScreen />;
  }

  return (
    <AppErrorBoundary>
      <AuthenticatedShell />
    </AppErrorBoundary>
  );
}

function AuthenticatedShell() {
  const { activeSection, toggleSidebar } = usePanelStore();
  const { activeProject } = useStudioStore();
  const [isDiagOpen, setIsDiagOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  const renderContent = () => {
    switch (activeSection) {
      case 'studio':
        return <DashboardPage />;
      case 'projects':
        return <ProjectsPage />;
      case 'templates':
        return <TemplatesPage />;
      case 'assets':
        return <AssetsPage />;
      case 'exports':
        return <ExportsPage />;
      default:
        return <DashboardPage />;
    }
  };

  if (activeProject) {
    return (
      <div className="h-screen w-screen overflow-hidden bg-[#0a0a0a] text-gray-200 font-montserrat flex flex-col">
        <EditorPage />
        <DevModeIndicator />
        <BugReportButton />
        <DiagnosticToggleFab isOpen={isDiagOpen} onClick={() => setIsDiagOpen((o: boolean) => !o)} />
        <DiagnosticWidget isOpen={isDiagOpen} onClose={() => setIsDiagOpen(false)} />
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden bg-[#0a0a0a] text-gray-200 font-montserrat flex">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {renderContent()}
      </div>
      <DevModeIndicator />
      <BugReportButton />
      <DiagnosticToggleFab isOpen={isDiagOpen} onClick={() => setIsDiagOpen(o => !o)} />
      <DiagnosticWidget isOpen={isDiagOpen} onClose={() => setIsDiagOpen(false)} />
    </div>
  );
}

export default App;
